import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { supabase } from '../../lib/supabaseClient';
import Seo from '../../components/common/Seo';
import ResponsiveImage from '../../components/common/ResponsiveImage';

export default function CreatorProfiles() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('creators').select('*').order('created_at', { ascending: false });
      if (!error && data?.length) setCreators(data);
      if (!error && !data?.length) setCreators([]);
      setLoading(false);
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-game-black text-white">
      <Seo
        title="Creators Hall Of Fame"
        description="Meet the creators and guests featured on GameMeetsWorld."
        path="/creators"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Creators', path: '/creators' }
        ]}
      />
      <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-20 text-center"
          >
            <h1 className="text-6xl md:text-7xl font-bold mb-4">
              <span className="text-accent-green">Hall of Fame</span>
            </h1>
            <p className="text-text-secondary text-lg">
              Celebrating the guests who came on the stream
            </p>
          </motion.div>

          {/* Creator Grid - Hall of Fame Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {loading && Array.from({ length: 8 }).map((_, idx) => (
              <div key={`creator-skeleton-${idx}`} className="group animate-pulse">
                <div className="relative w-full overflow-hidden rounded-2xl aspect-square bg-white/10 border-2 border-white/10" />
                <div className="h-6 w-2/3 bg-white/10 rounded mx-auto mt-6" />
              </div>
            ))}
            {creators.map((creator, idx) => (
              <motion.div
                key={creator.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group"
              >
                {/* Image Container */}
                <div className="relative w-full overflow-hidden rounded-2xl aspect-square bg-game-grey border-2 border-accent-green/30 group-hover:border-accent-green transition-all duration-300">
                  <ResponsiveImage
                    src={creator.image_url || creator.image}
                    alt={creator.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    sizes="(min-width: 1024px) 22vw, (min-width: 768px) 45vw, 92vw"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                {/* Creator Name */}
                <h3 className="text-center text-xl font-bold mt-6 group-hover:text-accent-green transition-colors">
                  {creator.display_name || creator.displayName}
                </h3>
              </motion.div>
            ))}
          </div>
          {!loading && creators.length === 0 && (
            <p className="text-center text-white/60 mt-8">
              No Hall of Fame entries yet.
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
